/*
 * A copy of the dispatch console's component of the same name.
 *
 * `02 Console Shell v3.dc.html` draws one sign-out dialog for the platform rather than one per
 * console, so the copy is this file's own, word for word.
 *
 * A copy rather than a shared package: see `docs/adr/0021-two-consoles-and-no-shared-shell.md`.
 */
import { useEffect, useRef, type ReactNode } from "react";

/**
 * The sign-out confirmation, from `02 Console Shell v3.dc.html`, `signoutOpen`.
 *
 * Asked rather than done, which is the design's judgement and the right one: the account menu's last
 * row is the one whose effect an operator cannot undo by clicking again, and on an admin
 * console it is next to four rows that open panels.
 *
 * "all consoles on this device" is accurate. Signing out ends the provider's session, not just this
 * page's tokens — otherwise the next sign-in would complete without asking for anything, which is
 * not what the person who clicked it meant and is actively wrong on a shared machine.
 *
 * Two things the design draws are not here, and both are the same absence rather than a shortcut:
 * the list of this operator's other sessions, and the "Also sign out my other 2 devices" checkbox
 * that revokes them with reason `user_signed_out_everywhere`. IAM-04 publishes both operations —
 * `GET /v1/sessions` and `POST /v1/sessions/{sessionId}/revocations` — and has no rows for them,
 * because nothing creates a session record: ADR 0014 notes that IAM-04 "records sessions created by
 * the identity authority, and there will now be one", and the recording has not been built. A device
 * list drawn against an empty listing says "no other devices" to an operator who has three.
 * Recorded as Q-18 and as R-11.
 */

export interface SignOutDialogProps {
  readonly onConfirm: () => void;
  readonly onDismiss: () => void;
}

export function SignOutDialog({ onConfirm, onDismiss }: SignOutDialogProps): ReactNode {
  const stay = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // The safe control takes focus, not the destructive one. A dialog that opens with "Sign out"
    // focused signs an operator out of their shift on the Enter key they were already pressing.
    stay.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onDismiss();
    };
    document.addEventListener("keydown", onKey);
    return (): void => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onDismiss]);

  return (
    <div className="signout">
      <div role="alertdialog" aria-label="Sign out" className="signout__dialog">
        <div className="signout__body">
          <span aria-hidden="true" className="signout__glyph">
            logout
          </span>
          <div className="signout__text">
            <h2 className="signout__headline">Sign out of all consoles on this device?</h2>
            <span className="signout__detail">
              Anything you've submitted is already on the server. Unsaved edits in open tabs will be
              lost.
            </span>
          </div>
        </div>
        <div className="signout__actions">
          <button type="button" ref={stay} className="signout__stay" onClick={onDismiss}>
            Stay signed in
          </button>
          <button type="button" className="signout__confirm" onClick={onConfirm}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
